import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@/components/common/Button";
import { CheckOutlined, PlusOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import AddSecurityQuestion from "@/pages/settings/components/manage_kyc/Add_Security_Questions";
import AnswerSecurityQuestionModal from "@/pages/settings/components/manage_kyc/Answer_Security_Questions_Modal";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import {
  fetchUserRecoveryQuestionsAsync,
  selectUserRecoveryQuestions,
  selectSettingsError,
  selectSettingsSuccess,
  clearSettingsState,
} from "@/store/redux/slices/settings/settings_slice";
import PropTypes from "prop-types";

const SecurityQuestions = ({ isModalVisible, setIsModalVisible }) => {
  const getQuestionText = (item, index) => {
    if (item?.question && typeof item.question === "object" && item.question.question) {
      return item.question.question;
    }
    if (typeof item?.questionText === "string" && item.questionText.trim()) {
      return item.questionText;
    }
    if (typeof item?.question === "string" && item.question.trim()) {
      // In some APIs `question` is the text; in others it's an ID.
      // If it looks like an ObjectId-like string, don't display it as text.
      const looksLikeId = /^[a-f0-9]{24}$/i.test(item.question.trim());
      if (!looksLikeId) return item.question;
    }
    return `Security Question ${index + 1}`;
  };

  const getAnswerText = (item) => {
    return (
      item?.answer ||
      item?.recoveryAnswer ||
      item?.userAnswer ||
      ""
    );
  };

  const canManageQuestion = (item) => {
    // We can submit updates with the recovery-question link _id,
    // but we can only prefill question/answer if backend returns them.
    return Boolean(item?._id);
  };

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isAnswerModalVisible, setIsAnswerModalVisible] = useState(false);
  const [isNewQuestion, setIsNewQuestion] = useState(false);
  const dispatch = useDispatch();
  const userRecoveryQuestions = useSelector(selectUserRecoveryQuestions);
  const error = useSelector(selectSettingsError);
  const success = useSelector(selectSettingsSuccess);

  useEffect(() => {
    const fetchQuestions = async () => {
      await dispatch(fetchUserRecoveryQuestionsAsync());
    };
    fetchQuestions();
  }, [dispatch]);

  // Handle selecting a question from the "Add New" dialog.
  // so we pass the raw question (from the master list) and mark it as new.
  const handleAddNewQuestionSelect = (question) => {
    setSelectedQuestion({
      _id: question._id,
      question: question.question,
      answer: "",
      isEditMode: false,
    });
    setIsNewQuestion(true);
    setIsAnswerModalVisible(true);
  };

  // Handle clicking on the MANAGE button (update existing answer).
  const handleManageQuestion = (item) => {
    const index = userRecoveryQuestions.findIndex((q) => q?._id === item?._id);
    const questionText = getQuestionText(item, index >= 0 ? index : 0);
    const answer = String(getAnswerText(item) || "");

    setSelectedQuestion({
      _id: item._id,
      question: questionText,
      answer: answer,
      isEditMode: true,
    });
    setIsNewQuestion(false);
    setIsAnswerModalVisible(true);
  };

  const handleAddNewQuestion = () => {
    setSelectedQuestion(null);
    setIsModalVisible(true);
  };

  const handleAnswerModalClose = () => {
    setIsAnswerModalVisible(false);
    if (isModalVisible) {
      setIsModalVisible(false);
    }
    setIsNewQuestion(false);
  };

  const handleSuccessClose = () => {
    dispatch(clearSettingsState());
    setSelectedQuestion(null);
  };

  const handleErrorClose = () => {
    dispatch(clearSettingsState());
  };

  return (
    <div className="mt-4">
      <h2 className="mb-2 text-bold text-lighterGrey">
        Below are your security questions
      </h2>
      <p className="mb-6 text-regular text-lighterGrey">
        Security questions can be your alternative methods of regaining access
        to your account when you have a major issue.
      </p>

      {/* Security Questions List */}
      {userRecoveryQuestions.length > 0 ? (
        <div className="space-y-4">
          {userRecoveryQuestions.map((item, index) => {
            const questionText = getQuestionText(item, index);
            const isManageDisabled = !canManageQuestion(item);

            return (
              <div
                key={item._id}
                className="p-4 rounded-lg lg:rounded-none max-lg:border lg:border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 max-w-[65%]">
                    <span className="text-[20px] text-bold shrink-0">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <h3 className="mb-1 font-semibold text-regular line-clamp-2">
                        {questionText}
                      </h3>
                      <div className="flex items-center gap-2 lg:hidden">
                        <div className="w-5 h-5 rounded-full bg-green flex items-center justify-center">
                          <CheckOutlined className="text-white text-[12px]" />
                        </div>
                        <span className="text-xs font-bold">Setup</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-5 shrink-0 ml-4">
                    <div className="items-center hidden gap-2 lg:flex">
                      <div className="w-5 h-5 rounded-full bg-green flex items-center justify-center">
                        <CheckOutlined className="text-white text-[12px]" />
                      </div>
                      <span className="text-bold font-bold">Setup</span>
                    </div>
                    <Button
                      text="MANAGE"
                      icon={
                        <WebIcon
                          icon="Gear"
                          size="large"
                          className="ml-3 w-[18px] h-[18px]"
                        />
                      }
                      iconPosition="right"
                      customBgColor="white"
                      customTextColor="black"
                      fontWeight="normal"
                      className="border border-primary h-[40px] text-small"
                      disabled={isManageDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isManageDisabled) return;
                        handleManageQuestion(item);
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-regular text-gray-500">
            No security questions added
          </p>
        </div>
      )}

      {/* Add New Question Button - Opens Add Security Question modal */}
      <Button
        text="ADD NEW QUESTION"
        icon={<PlusOutlined className="ml-3 text-primary" />}
        iconPosition="left"
        customBgColor="white"
        customTextColor="primary"
        className="mt-6 border border-primary w-full h-[36px] lg:hidden"
        onClick={handleAddNewQuestion}
      />

      {/* Add Security Question Dialog */}
      <AddSecurityQuestion
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSelect={handleAddNewQuestionSelect}
      />

      {/* Answer Security Question Dialog */}
      {selectedQuestion && (
        <AnswerSecurityQuestionModal
          isVisible={isAnswerModalVisible}
          onClose={handleAnswerModalClose}
          question={selectedQuestion.question}
          questionId={selectedQuestion._id}
          existingAnswer={selectedQuestion.answer || ""}
          isEditMode={selectedQuestion.isEditMode}
          isNewQuestion={isNewQuestion}
        />
      )}

      {/* Success Modal - handled at parent level so it can show after answer modal closes */}
      <Success
        isVisible={success}
        onClose={handleSuccessClose}
        header={selectedQuestion?.isEditMode ? "Answer Updated" : "Answer Submitted"}
        paragraph={
          selectedQuestion?.isEditMode
            ? "Your security question answer has been updated successfully."
            : "Your security question answer has been submitted successfully."
        }
        buttonText="Close"
      />

      {/* Error Modal - handled at parent level */}
      <Error
        isVisible={!!error}
        onClose={handleErrorClose}
        header="Failed to Save Answer"
        paragraph={
          typeof error === "string"
            ? error
            : error?.message ||
              "An error occurred while saving your answer. Please try again."
        }
        buttonText="Try Again"
      />
    </div>
  );
};

SecurityQuestions.propTypes = {
  isModalVisible: PropTypes.bool,
  setIsModalVisible: PropTypes.func,
};

export default SecurityQuestions;
